import { useCallback } from "react";
import { useShipToApi } from "./useShipToApi";
import { useSessionSetAtom } from "../useSessionSetAtom";
import { createReadGuard } from "./_helpers/readGuard";
import { assertArrayPayload } from "@utils/apiPayload";
import {
    MAX_SUGGESTIONS_PER_SECTION,
    type ProductSuggestion,
    type ProductSuggestionsStatus,
} from "@models/productSuggestion";
import {
    productSuggestionsAtom,
    productSuggestionsStatusAtom,
} from "@/store/productSuggestionAtoms";

// GET /products/suggest (#227). A failed read — 404 included — settles the status on `failed`,
// and the Products section of the search autocomplete shows its "Couldn't load products" message.
const SUGGEST_ENDPOINT = "/products/suggest";

// Which read is allowed to publish. Module-scope for the same reason useProductFacetApi's is:
// the atoms are module-scope (no Jotai Provider in the app) while any caller's guard would be
// per-mount, so a sequence — not a per-caller "cancelled" flag — is what answers "has a newer
// read taken over?". A suppressed write always has a successor that publishes in its place.
// Latest-only (`isLatest`); a clear supersedes every read on the wire.
const suggestReads = createReadGuard();

// The request that is still on the wire, if any. Each new query aborts it: a typeahead that lets
// four superseded requests run to completion wastes the shopper's connection on answers nobody
// will see. The sequence above stays as well — an abort that races its own response still needs
// the guard, because fetch may already have resolved by the time abort() runs.
let inFlightController: AbortController | null = null;

export const useProductSuggestApi = () => {
    const { callShipToApi } = useShipToApi();
    // Session-bound: a read answering after sign-out publishes nothing, and the
    // status stays at the "idle" endSessionAtom reset it to.
    const setSuggestions = useSessionSetAtom(productSuggestionsAtom);
    const setStatus = useSessionSetAtom(productSuggestionsStatusAtom);

    // Product rows for the search autocomplete (#227). skipErrorNotification: this is a
    // SECONDARY read that decorates a search box which works fine without it — a failure costs
    // the shopper a list of shortcuts, not their search, so it must not toast over a working
    // input. The section degrades to an inline "Couldn't load products" message (see SearchPanel).
    const getProductSuggestions = useCallback(
        async (q: string): Promise<ProductSuggestion[]> => {
            const { isLatest } = suggestReads.begin();

            inFlightController?.abort();
            const controller = new AbortController();
            inFlightController = controller;

            // Stale-while-revalidate: while the next query's rows are on the wire the previous
            // query's rows stay on screen rather than the section blinking empty on every
            // keystroke. Only a first load or a retry after failure has nothing to hold.
            setStatus((previous) =>
                previous === "loaded" ? "loaded" : "loading",
            );

            // Decided in the try, PUBLISHED in the finally — the apiHooks folder's shape,
            // adapted to a three-valued outcome. The pessimistic default is what keeps "loading"
            // from absorbing: every exit that is not a completed read is a failure, and an
            // aborted read is simply a superseded one, which isLatest() already declines to
            // publish.
            let outcome: ProductSuggestionsStatus = "failed";

            // One 8: the section's cap is the request's limit.
            const params = new URLSearchParams({
                q,
                limit: String(MAX_SUGGESTIONS_PER_SECTION),
            });

            try {
                const response = await callShipToApi<ProductSuggestion[]>(
                    `${SUGGEST_ENDPOINT}?${params.toString()}`,
                    { skipErrorNotification: true, signal: controller.signal },
                );

                // A 2xx whose body is not an array (an empty 200, an HTML page from a proxy) is
                // a failure, not rows: published, it would reach Autocomplete as something it
                // cannot map. The throw is what makes the finally publish `failed` like any other.
                assertArrayPayload(SUGGEST_ENDPOINT, response);

                if (isLatest()) setSuggestions(response);
                outcome = "loaded";

                return response;
            } finally {
                if (inFlightController === controller) inFlightController = null;
                if (isLatest()) {
                    // Unlike the facets, a failed read does NOT keep the previous payload:
                    // those rows answered a different query, and the section renders the
                    // failure instead of them anyway, so holding them would only let the
                    // NEXT query's loading state show rows from two queries ago.
                    if (outcome === "failed") setSuggestions([]);
                    setStatus(outcome);
                }
            }
        },
        [callShipToApi, setSuggestions, setStatus],
    );

    // The query dropped below the two-character floor (cleared, or the panel closed), or the
    // panel unmounted. The stale-while-revalidate hold above exists so the NEXT query of the
    // same session does not flash; rows kept past this point would be a previous query's — or a
    // previous session's — showing under the next two characters typed. Below the floor the
    // Products section is absent anyway, so this is the one moment the hold can be released
    // with nothing to flash.
    //
    // It also retires whatever is on the wire: a response landing after the clear would put
    // those rows straight back. Taking the sequence over means that read's finally publishes
    // nothing, and the abort stops it costing the connection; the clear is its successor.
    const clearProductSuggestions = useCallback(() => {
        suggestReads.supersede();
        inFlightController?.abort();
        inFlightController = null;
        setSuggestions((previous) => (previous.length === 0 ? previous : []));
        setStatus("idle");
    }, [setSuggestions, setStatus]);

    return {
        getProductSuggestions,
        clearProductSuggestions,
    };
};
